'use client'

import { useState } from 'react'
import { AnimatePresence } from 'motion/react'

import Link from '@/components/ui/Link'
import Portal from '@/components/ui/Portal'
import DealTracker from '@/components/content/deals/deal/DealTracker'

import type { getPrimaryButton } from '@/components/content/app/helpers'
import type { App, Deal } from '@/payload-types'

type PrimaryButtonProps = {
  primaryButton: ReturnType<typeof getPrimaryButton>
  primaryDeal?: Deal
  onClick?: () => void
  app: Pick<App, 'id' | 'slug' | 'name' | 'affiliateLink' | 'partnershipTier'>
  className?: string
  children?: React.ReactNode
  decorative?: boolean
}

export default function PrimaryButton({
  primaryButton,
  primaryDeal,
  onClick,
  app,
  className,
  children,
  decorative = false,
}: PrimaryButtonProps) {
  const [dealTracked, setDealTracked] = useState(false)

  const handleClick =
    primaryButton.isDeal && primaryDeal
      ? () => {
          localStorage.setItem('efficient:claim', 'true')
          setDealTracked(true)
          onClick?.()
        }
      : onClick
        ? onClick
        : undefined

  return (
    <>
      <Link
        href={primaryButton.destination}
        target={primaryButton.opensInNewTab ? '_blank' : undefined}
        passReferrerUrl={primaryButton.passReferrerUrl}
        className={className}
        onClick={handleClick}
        sponsored={primaryButton.sponsored}
        tabIndex={decorative ? -1 : undefined}
        aria-hidden={decorative ? true : undefined}
      >
        {children}
      </Link>

      <Portal>
        <AnimatePresence>
          {dealTracked && primaryDeal && (
            <DealTracker deal={primaryDeal} app={app} close={() => setDealTracked(false)} />
          )}
        </AnimatePresence>
      </Portal>
    </>
  )
}
